import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '../../components/button';
import CustomInput from '../../components/customInput/CustomInput';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import EyeIcon from '../../static/image/eyeIcon.svg';
import EditIcon from '../../static/image/editIcon.svg';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import CreateRole from '../createRole/CreateRole';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoles, deleteRole, fetchRoleDetails } from '../../redux/roleSlice';
import {
  EDIT_MODEL_CREATE,
  EDIT_MODEL_VIEW,
  EDIT_MODEL_EDIT,
  PERMISSION_CONSTANTS,
  PERMISSION_VALUES,
  MENU_IDS,
  ROUTES
} from '../../utils/constant';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { formatDatetime } from '../../utils/dateUtils';
import { API_GET_ACCOUNT_ROLES } from '../../utils/apis';
import { decodedJWTPayload } from '../../utils/commonUtil';

function ManageRole() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const location = useLocation();

  const [inputValue2, setInputValue2] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10
  });
  const [selectedRole, setSelectedRole] = useState(null);

  const getEditModeFromRoute = () => {
    if (location.pathname.includes('/create')) return EDIT_MODEL_CREATE;
    if (location.pathname.includes('/edit/')) return EDIT_MODEL_EDIT;
    if (location.pathname.includes('/view/')) return EDIT_MODEL_VIEW;
    return undefined;
  };

  const [editMode, setEditMode] = useState(getEditModeFromRoute());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [permission, setPermission] = useState('');

  const dispatch = useDispatch();
  const { data: roles, loading, roleDetails } = useSelector((state) => state.roles);

  useEffect(() => {
    fetchRolesData();
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const jwtPattern = PERMISSION_CONSTANTS.CHECKS.getJwtPattern(MENU_IDS.ROLE_AND_PERMISSION);
        const match = mnId.match(jwtPattern);
        if (match) {
          setPermission(match[1]);
        }
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const newEditMode = getEditModeFromRoute();
    setEditMode(newEditMode);

    if ((newEditMode === EDIT_MODEL_EDIT || newEditMode === EDIT_MODEL_VIEW) && roleId) {
      handleFetchRoleDetails(roleId);
    } else if (newEditMode === EDIT_MODEL_CREATE) {
      setSelectedRole(null);
    }
  }, [location.pathname, roleId]);

  const fetchRolesData = (paginationParams = pagination, roleName = '') => {
    const { page, size } = paginationParams;
    let url = `${API_GET_ACCOUNT_ROLES}?page=${page}&size=${size}`;
    if (roleName) {
      url += `&roleName=${encodeURIComponent(roleName)}`;
    }
    dispatch(fetchRoles(url));
  };

  useEffect(() => {
    if (roleDetails) {
      setSelectedRole(roleDetails);
      setIsFetchingDetails(false);
    }
  }, [roleDetails]);

  const handleFetchRoleDetails = async (roleId) => {
    try {
      setIsFetchingDetails(true);
      await dispatch(fetchRoleDetails(roleId));
    } catch (error) {
      console.error('Failed to fetch role details:', error);
      navigate(ROUTES.ROLE_AND_PERMISSION);
    }
  };

  const columns = [
    {
      name: 'Role Name',
      cell: (row) => <div>{row.roleName}</div>
    },
    {
      name: 'Status',
      cell: (row) => <div>{row.isActive ? 'Active' : 'In-Active'}</div>
    },
    {
      name: 'Created By',
      cell: (row) => <div>{row.createdBy}</div>
    },
    {
      name: 'Created Date/Time',
      cell: (row) => <div>{formatDatetime(row.createdDatetime)}</div>
    },
    {
      name: 'Updated By',
      cell: (row) => <div>{row.updatedBy}</div>
    },
    {
      name: 'Updated Date/Time',
      cell: (row) => <div>{formatDatetime(row.updatedDatetime)}</div>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img
            src={EyeIcon}
            alt="Eye"
            onClick={() => handleViewRole(row.roleId)}
            style={{ cursor: 'pointer' }}
          />
          <img
            src={EditIcon}
            alt="Edit"
            onClick={() => handleEditRole(row.roleId)}
            style={{
              opacity: permission.includes(PERMISSION_VALUES.WRITE) ? 1 : 0.5,
              cursor: permission.includes(PERMISSION_VALUES.WRITE) ? 'pointer' : 'default'
            }}
          />
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDeleteClick(row)}
            style={{
              opacity: permission === PERMISSION_VALUES.FULL_ACCESS ? 1 : 0.5,
              cursor: permission === PERMISSION_VALUES.FULL_ACCESS ? 'pointer' : 'default',
            }}
          />
        </div>
      ),
    }
  ];

  const handlePageNumberChange = (page) => {
    const newPagination = { ...pagination, page: page - 1 };
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    const newPagination = { page: page - 1, size: newPerPage };
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
  };

  const openCreateRolePage = () => {
    if (permission.includes(PERMISSION_VALUES.WRITE)) {
      navigate(ROUTES.ROLE_CREATE);
    }
  };

  const closeCreateRolePage = () => {
    navigate(ROUTES.ROLE_AND_PERMISSION);
  };

  const handleEditRole = (roleId) => {
    if (permission.includes(PERMISSION_VALUES.WRITE)) {
      navigate(`/dashboard/role-and-permission/edit/${roleId}`);
    }
  };

  const handleViewRole = (roleId) => {
    navigate(`/dashboard/role-and-permission/view/${roleId}`);
  };

  const refetchRoles = () => {
    fetchRolesData(pagination, inputValue2);
  };

  const handleCancel = () => {
    navigate(ROUTES.ROLE_AND_PERMISSION);
  };

  const handleRoleCreated = () => {
    refetchRoles();
    navigate(ROUTES.ROLE_AND_PERMISSION);
  };

  const handleRoleUpdated = () => {
    refetchRoles();
    navigate(ROUTES.ROLE_AND_PERMISSION);
  };

  const handleDeleteClick = (role) => {
    if (permission === PERMISSION_VALUES.FULL_ACCESS) {
      setSelectedRole(role);
      setShowDeleteModal(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteRole(selectedRole.roleId))
      .unwrap()
      .then(() => {
        refetchRoles();
        setShowDeleteModal(false);
        if (editMode) {
          navigate(ROUTES.ROLE_AND_PERMISSION);
        }
      })
      .catch((error) => {
        console.error(error);
        setShowDeleteModal(false);
      });
  };

  const handleSearch = () => {
    const newPagination = { ...pagination, page: 0 };
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
    if (editMode) {
      navigate(ROUTES.ROLE_AND_PERMISSION);
    }
  };

  const handleReset = () => {
    setInputValue2('');
    const newPagination = { ...pagination, page: 0 };
    setPagination(newPagination);
    fetchRolesData(newPagination);
    if (editMode) {
      navigate(ROUTES.ROLE_AND_PERMISSION);
    }
  };

  return (
    <>
      {!editMode && (
        <div className="manage-user-container">
          <div style={{ padding: '30px' }}>
            <div>Manage Role</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px', gap: '20px', flexWrap: 'wrap' }}>
              <Button
                className="manage-user-btn"
                onClick={openCreateRolePage}
                style={{
                  opacity: permission.includes(PERMISSION_VALUES.WRITE) ? 1 : 0.5,
                  pointerEvents: permission.includes(PERMISSION_VALUES.WRITE) ? 'auto' : 'none',
                }}
              >
                CREATE ROLE
              </Button>
              <div>
                <CustomInput
                  placeholder="Role Name"
                  style={{ width: '240px', height: '45px' }}
                  value={inputValue2}
                  onChange={(e) => setInputValue2(e.target.value)}
                />
              </div>
              <Button className="manage-user-btn" onClick={handleSearch}>
                SEARCH
              </Button>
              <Button className="manage-user-btn" onClick={handleReset}>
                RESET
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              <CustomPaginationTable
                data={roles.content || []}
                totalRows={roles.totalElements || 0}
                loading={loading}
                perPage={pagination.size}
                currentPage={pagination.page + 1}
                onPageChange={handlePageNumberChange}
                columns={columns}
                onPerRowsChange={handleRowsPerPageChange}
                isHiddenPag={false}
              />
            </div>
          </div>
        </div>
      )}
      {editMode && (
        <CreateRole
          editMode={editMode}
          role={selectedRole}
          onCancel={handleCancel}
          onRoleCreated={handleRoleCreated}
          onRoleUpdated={handleRoleUpdated}
        />
      )}
      <CommonDeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default ManageRole;